import {
  Component,
  computed,
  effect,
  inject,
  model,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import {
  ChipInputComponent,
  TabItem,
  TabMenuComponent,
  TabMenuStyle,
} from '@tiltgroup/web-components';
import {
  ReactiveFormsModule,
  FormBuilder,
  Validators,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { MatInput } from '@angular/material/input';
import { ErrorStateMatcher } from '@angular/material/core';

enum TrackingMode {
  PRO_NUMBER = 'Pro #',
  CUSTOMER_REF = 'Customer Unique Reference',
}

interface TrackingForm {
  trackingNumbers: FormControl<string[] | null>;
  customerReference?: FormControl<string>;
}

export class InputStateMatcher implements ErrorStateMatcher {
  isErrorState(control: FormControl | null) {
    return control && control.invalid && control.touched;
  }
}

@Component({
  selector: 'lt-tracker',
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    ChipInputComponent,
    TabMenuComponent,
    MatInput,
  ],
  templateUrl: './tracker.component.html',
  // Keeps pre-Angular 22 (Default) change detection, which this component was written for.
  // TODO: migrate to OnPush.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./tracker.component.scss'],
})
export class TrackerComponent {
  trackingNumbers = model(<string[]>[]);
  customerReference = model<string>('');

  trackingMode = signal(TrackingMode.PRO_NUMBER);

  searchTabs = computed<TabItem[]>(() => {
    return [
      {
        title: TrackingMode.PRO_NUMBER,
        active: this.trackingMode() === TrackingMode.PRO_NUMBER,
        clickFn: () => {
          this.setTrackingMode(TrackingMode.PRO_NUMBER);
        },
      },
      {
        title: TrackingMode.CUSTOMER_REF,
        active: this.trackingMode() === TrackingMode.CUSTOMER_REF,
        clickFn: () => {
          this.setTrackingMode(TrackingMode.CUSTOMER_REF);
        },
      },
    ];
  });

  errorStateMatcher = new InputStateMatcher();

  protected readonly TabMenuStyle = TabMenuStyle;
  protected readonly TrackingMode = TrackingMode;

  private customerRefControl = new FormControl('', Validators.required);

  private fb = inject(FormBuilder);

  form: FormGroup<TrackingForm> = this.fb.group({
    trackingNumbers: this.fb.control<string[] | null>(this.trackingNumbers(), {
      validators: [Validators.required, Validators.maxLength(50)],
    }),
  });

  constructor() {
    effect(() => {
      if (this.customerReference()) {
        this.setTrackingMode(TrackingMode.CUSTOMER_REF);
        this.form.controls.customerReference.setValue(this.customerReference());
      } else {
        this.setTrackingMode(TrackingMode.PRO_NUMBER);
      }
      this.form.controls.trackingNumbers.setValue(this.trackingNumbers());
    });
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;

    this.trackingNumbers.set(value.trackingNumbers);
    this.customerReference.set(value.customerReference ?? null);
  }

  private setTrackingMode(trackingMode: TrackingMode) {
    this.trackingMode.set(trackingMode);

    if (trackingMode === TrackingMode.CUSTOMER_REF) {
      this.form.addControl('customerReference', this.customerRefControl);
    } else {
      this.form.removeControl('customerReference');
    }

    this.form.reset();
  }
}
